import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const fmtMln = (n) => `€${((Number(n) || 0) / 1e6).toFixed(2)}mln`;
const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
};

const COUNTRY_META = {
    china: { flag: "🇨🇳", label: "Cina", color: "rgb(var(--tc-e8222b))" },
    arabia: { flag: "🇸🇦", label: "Arabia", color: "rgb(var(--tc-1e7d3c))" },
};

const STATUS_LABEL = {
    pending: { t: "In attesa", c: "rgb(var(--tc-ffb020))" },
    accepted: { t: "Accettata", c: "rgb(var(--tc-7ce38b))" },
    rejected: { t: "Rifiutata", c: "rgb(var(--tc-8f95a3))" },
    expired: { t: "Scaduta", c: "rgb(var(--tc-5c6370))" },
};

export default function AdminBots() {
    const [sessions, setSessions] = useState([]);
    const [treasury, setTreasury] = useState(0);
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // form
    const [country, setCountry] = useState("china");
    const [budgetMln, setBudgetMln] = useState("100");
    const [perBatch, setPerBatch] = useState(2);
    const [hours, setHours] = useState(8);
    const [minPct, setMinPct] = useState(25);
    const [maxPct, setMaxPct] = useState(130);
    const [ttl, setTtl] = useState(48);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const s = await api.get("/bots/sessions");
            setSessions(s.data.sessions || []);
            setTreasury(s.data.treasury_balance || 0);
            const o = await api.get("/bots/offers", { params: { limit: 100 } });
            setOffers(o.data.offers || []);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const openSession = async () => {
        setBusy(true); setError(null);
        try {
            await api.post("/bots/sessions", {
                country,
                budget: Math.round(parseFloat(budgetMln || "0") * 1e6),
                offers_per_batch: Number(perBatch),
                hours_between_batches: Number(hours),
                min_pct: Number(minPct),
                max_pct: Number(maxPct),
                offer_ttl_hours: Number(ttl),
            });
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        } finally { setBusy(false); }
    };

    const runBatch = async (id) => {
        try { await api.post(`/bots/sessions/${id}/run-batch`); await load(); }
        catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    const closeSession = async (id) => {
        if (!confirm("Chiudere la sessione? Le offerte in attesa decadono e il residuo torna al Tesoro OP.")) return;
        try { await api.post(`/bots/sessions/${id}/close`); await load(); }
        catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const meta = COUNTRY_META[country];

    return (
        <div data-testid="admin-bots">
            <div className="op-label" style={{ color: "rgb(var(--tc-e8222b))" }}>// MERCATO ESTERO</div>
            <h2 className="op-heading text-3xl mt-2">🌏 Cina / Arabia</h2>
            <p className="text-t8f95a3 text-sm mt-2 max-w-3xl">
                Acquirenti esteri finanziati dal <span className="text-tffffff">Tesoro OP</span>. Stanzia un budget: viene
                scalato subito dal Tesoro e diventa la Cassa. A scaglioni (mentre una finestra di mercato è aperta) il bot
                fa offerte sui giocatori in vetrina, <span className="text-tffffff">privilegiando le squadre povere con giocatori a basso valore TM</span>.
                Il presidente accetta o rifiuta. Giocatore ceduto → torna svincolato e la squadra incassa.
            </p>
            <div className="op-mono text-xs text-t8f95a3 mt-2">
                Tesoro OP disponibile: <span className="text-t7ce38b" data-testid="treasury-balance">{fmtMln(treasury)}</span>
            </div>

            {error && <div className="op-card p-3 mt-4 text-tff4d4d text-sm" data-testid="bots-error">{error}</div>}

            {/* Open form */}
            <div className="op-card p-5 mt-6" data-testid="open-session-form">
                <div className="op-label mb-3" style={{ color: meta.color }}>// NUOVA SESSIONE</div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <Field label="Paese">
                        <select value={country} onChange={(e) => setCountry(e.target.value)}
                            className="op-input" data-testid="bot-country">
                            <option value="china">🇨🇳 Cina</option>
                            <option value="arabia">🇸🇦 Arabia</option>
                        </select>
                    </Field>
                    <Field label="Budget (mln €)">
                        <input type="number" min="1" value={budgetMln} onChange={(e) => setBudgetMln(e.target.value)}
                            className="op-input" data-testid="bot-budget" />
                    </Field>
                    <Field label="Offerte per scaglione">
                        <input type="number" min="1" max="20" value={perBatch} onChange={(e) => setPerBatch(e.target.value)}
                            className="op-input" data-testid="bot-perbatch" />
                    </Field>
                    <Field label="Ogni quante ore">
                        <input type="number" min="1" max="168" value={hours} onChange={(e) => setHours(e.target.value)}
                            className="op-input" data-testid="bot-hours" />
                    </Field>
                    <Field label="% minima del TM">
                        <input type="number" min="1" value={minPct} onChange={(e) => setMinPct(e.target.value)}
                            className="op-input" data-testid="bot-minpct" />
                    </Field>
                    <Field label="% massima del TM">
                        <input type="number" min="1" value={maxPct} onChange={(e) => setMaxPct(e.target.value)}
                            className="op-input" data-testid="bot-maxpct" />
                    </Field>
                    <Field label="Scadenza offerta (ore)">
                        <input type="number" min="1" value={ttl} onChange={(e) => setTtl(e.target.value)}
                            className="op-input" data-testid="bot-ttl" />
                    </Field>
                    <div className="flex items-end">
                        <button onClick={openSession} disabled={busy}
                            className="op-btn op-btn-primary w-full" data-testid="open-session-btn"
                            style={{ background: meta.color, borderColor: meta.color }}>
                            {busy ? "Apertura..." : `Apri ${meta.flag} ${meta.label}`}
                        </button>
                    </div>
                </div>
                <div className="op-mono text-[10px] text-t5c6370 mt-3" data-testid="bot-targeting-hint">
                    {country === "arabia"
                        ? "🇸🇦 ARABIA: offre a QUALSIASI giocatore (anche fuori vetrina), puntando ai più forti — VG e TM alti, cifre importanti. Indifferente al budget della squadra."
                        : "🇨🇳 CINA: offre sui giocatori IN VETRINA, privilegiando le squadre povere con giocatori a basso valore TM."}
                </div>
                <div className="op-mono text-[10px] text-t5c6370 mt-1">
                    Il bot opera solo mentre c&apos;è una finestra di mercato APERTA. La cifra esce subito dal Tesoro OP.
                </div>
            </div>

            {/* Sessions */}
            <div className="op-label mt-8 mb-3" style={{ color: "rgb(var(--tc-ffb020))" }}>// SESSIONI</div>
            {loading ? (
                <div className="text-t8f95a3 text-sm">Caricamento…</div>
            ) : sessions.length === 0 ? (
                <div className="op-card p-5 text-t8f95a3 text-sm">Nessuna sessione. Aprine una qui sopra.</div>
            ) : (
                <div className="grid lg:grid-cols-2 gap-3">
                    {sessions.map((s) => {
                        const m = COUNTRY_META[s.country] || COUNTRY_META.china;
                        const isOpen = s.status === "open";
                        const pct = (v) => s.allocated ? Math.min(100, Math.round((v / s.allocated) * 100)) : 0;
                        return (
                            <div key={s.id} className="op-card p-5" data-testid={`session-${s.id}`}>
                                <div className="flex items-center justify-between">
                                    <div className="op-heading text-xl">{m.flag} {m.label}</div>
                                    <span className="op-label text-[10px] px-2 py-0.5 border"
                                        style={{ color: isOpen ? "rgb(var(--tc-7ce38b))" : "rgb(var(--tc-8f95a3))", borderColor: isOpen ? "rgb(var(--tc-7ce38b) / 0.333)" : "rgb(var(--tc-8f95a3) / 0.333)" }}>
                                        {isOpen ? "APERTA" : "CHIUSA"}
                                    </span>
                                </div>
                                {/* Cassa live */}
                                <div className="grid grid-cols-4 gap-2 mt-4 text-center">
                                    <Stat label="Stanziato" value={fmtMln(s.allocated)} color="rgb(var(--tc-e0e0e0))" testId={`stat-allocated-${s.id}`} />
                                    <Stat label="Impegnato" value={fmtMln(s.committed)} color="rgb(var(--tc-ffb020))" testId={`stat-committed-${s.id}`} />
                                    <Stat label="Speso" value={fmtMln(s.spent)} color="rgb(var(--tc-ff7a1a))" testId={`stat-spent-${s.id}`} />
                                    <Stat label="Disponibile" value={fmtMln(s.available)} color="rgb(var(--tc-7ce38b))" testId={`stat-available-${s.id}`} />
                                </div>
                                {/* bar */}
                                <div className="mt-3 h-2 w-full bg-t16181c rounded overflow-hidden flex">
                                    <div style={{ width: `${pct(s.spent)}%`, background: "rgb(var(--tc-ff7a1a))" }} />
                                    <div style={{ width: `${pct(s.committed)}%`, background: "rgb(var(--tc-ffb020))" }} />
                                </div>
                                <div className="op-mono text-[10px] text-t5c6370 mt-3 flex flex-wrap gap-x-4 gap-y-1">
                                    <span>Offerte: {s.offers_per_batch}/scaglione · ogni {s.hours_between_batches}h</span>
                                    <span>Range: {s.min_pct}%–{s.max_pct}% TM</span>
                                    <span>TTL: {s.offer_ttl_hours}h</span>
                                    <span>Ultimo scaglione: {fmtDate(s.last_batch_at)}</span>
                                </div>
                                {isOpen && (
                                    <div className="flex gap-2 mt-4">
                                        <button onClick={() => runBatch(s.id)} className="op-btn op-btn-ghost text-xs" data-testid={`run-batch-${s.id}`}>
                                            ⚡ Genera scaglione ora
                                        </button>
                                        <button onClick={() => closeSession(s.id)} className="op-btn op-btn-ghost text-xs text-tff4d4d" data-testid={`close-session-${s.id}`}>
                                            Chiudi & rimborsa residuo
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Offers log */}
            <div className="op-label mt-8 mb-3" style={{ color: "rgb(var(--tc-2bb6ff))" }}>// OFFERTE ({offers.length})</div>
            {offers.length === 0 ? (
                <div className="op-card p-5 text-t8f95a3 text-sm">Ancora nessuna offerta generata.</div>
            ) : (
                <div className="op-card overflow-hidden">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-t22252a text-left">
                                <th className="px-4 py-3 op-label text-[10px] text-t2bb6ff">Giocatore</th>
                                <th className="px-4 py-3 op-label text-[10px] text-t2bb6ff">Squadra</th>
                                <th className="px-4 py-3 op-label text-[10px] text-t2bb6ff text-right">TM</th>
                                <th className="px-4 py-3 op-label text-[10px] text-t2bb6ff text-right">Offerta</th>
                                <th className="px-4 py-3 op-label text-[10px] text-t2bb6ff text-center">%TM</th>
                                <th className="px-4 py-3 op-label text-[10px] text-t2bb6ff text-center">Stato</th>
                                <th className="px-4 py-3 op-label text-[10px] text-t2bb6ff">Data</th>
                            </tr>
                        </thead>
                        <tbody>
                            {offers.map((o) => {
                                const st = STATUS_LABEL[o.status] || { t: o.status, c: "rgb(var(--tc-8f95a3))" };
                                const m = COUNTRY_META[o.country] || {};
                                return (
                                    <tr key={o.id} className="border-b border-t16181c" data-testid={`offer-row-${o.id}`}>
                                        <td className="px-4 py-2 text-tffffff">{m.flag} {o.player_name}</td>
                                        <td className="px-4 py-2 text-t8f95a3">{o.team_name || "—"}</td>
                                        <td className="px-4 py-2 text-right op-mono text-t8f95a3">{fmtMln(o.market_value)}</td>
                                        <td className="px-4 py-2 text-right op-mono text-tffffff">{fmtMln(o.amount)}</td>
                                        <td className="px-4 py-2 text-center op-mono text-tffb020">{o.pct}%</td>
                                        <td className="px-4 py-2 text-center"><span className="op-mono text-[11px]" style={{ color: st.c }}>{st.t}</span></td>
                                        <td className="px-4 py-2 op-mono text-[10px] text-t5c6370">{fmtDate(o.created_at)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="op-label text-[10px] text-t8f95a3 block mb-1">{label}</span>
            {children}
        </label>
    );
}

function Stat({ label, value, color, testId }) {
    return (
        <div data-testid={testId}>
            <div className="op-label text-[9px] text-t5c6370">{label}</div>
            <div className="op-mono text-sm font-bold mt-1" style={{ color }}>{value}</div>
        </div>
    );
}
